import React, { useState } from "react";
import { useIntl } from "react-intl";
import cn from "classnames";

// Mui
import {
    Box,
    Typography,
    TableRow,
    TableCell,
    IconButton,
    Tooltip,
    Collapse,
    Button,
    LinearProgress,
} from "@mui/material";

// Icons
import icons from "enums/icons";
import InfoIcon from "@mui/icons-material/Info";
import RestoreIcon from "@mui/icons-material/Restore";

// Utils
import { formatDistanceToNow } from "date-fns";

// Styles
import UserTableRowStyles from "./UserTableRow.styles";

// Components
import { TeamChips } from "../TeamChips";
import CustomIcon from "components/CustomIcon";
import { InfoWithIcon } from "pages/ClientHub/components";
import NavButton from "components/HeaderNavigation/NavButton";
import { ConvertedAssessmentCentreUserData } from "pages/AssessmentCentre/adapters/converters";

const UserTableRow = ({
    data,
    Checkbox,
    handleShowReport,
    handleDownloadReport,
    handleTransferProfile,
    handleGetInfo,
}: {
    data: ConvertedAssessmentCentreUserData;
    Checkbox: React.ReactNode;
    handleShowReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleDownloadReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleTransferProfile: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleGetInfo: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
}) => {
    const intl = useIntl();
    const classes = UserTableRowStyles();
    const [shouldExpand, setShouldExpand] = useState(false);
    const [openPaceInfo, setOpenPaceInfo] = useState(false);

    const progress = data.assessments?.most_recent?.progress?.meta?.meter?.percentage || 0;

    const handleToggleExpand = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleStopPropagation(event);
        setShouldExpand(!shouldExpand);
    };

    const handleTogglePaceInfo = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleStopPropagation(event);
        setOpenPaceInfo(!openPaceInfo);
    };

    const handleStopPropagation = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
    };

    const PaceInfo = () => {
        return (
            <Box onClick={handleStopPropagation} className={classes.paceInfoWrapper}>
                <Box className={classes.paceInfoInner}>
                    <Typography className={classes.paceInfoDescription}>
                        {intl.formatMessage({ id: "userManagement.sharePace.tooltip.content" })}
                    </Typography>
                    <IconButton className={classes.paceInfoCloseButton} onClick={handleTogglePaceInfo}>
                        <CustomIcon icon={icons.close} />
                    </IconButton>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                    <Button className={classes.paceInfoRequestDataButton} onClick={() => console.log("here")}>
                        {intl.formatMessage({ id: "userManagement.sharePace.tooltip.buttonLabel" })}
                    </Button>
                </Box>
            </Box>
        );
    };

    const PaceStatus = ({ status }: { status: string }) => {
        return (
            <>
                {status === "Not Sharing" && (
                    <InfoWithIcon
                        startSlot={
                            <Box display="flex" alignItems="center">
                                <Tooltip
                                    arrow
                                    disableFocusListener
                                    disableHoverListener
                                    disableTouchListener
                                    open={openPaceInfo}
                                    enterDelay={500}
                                    leaveDelay={200}
                                    title={<PaceInfo />}
                                    classes={{ popper: classes.paceInfoPopper }}
                                >
                                    <IconButton onClick={handleTogglePaceInfo}>
                                        <CustomIcon
                                            icon={icons.paceIconNotShared}
                                            sx={{
                                                fontSize: 20,
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                        entityName={data.userName}
                    />
                )}
                {status === "Completed" && (
                    <Box display="flex" alignItems="center">
                        <IconButton onClick={handleTogglePaceInfo}>
                            <CustomIcon
                                icon={icons.paceIconColored}
                                sx={{
                                    fontSize: 20,
                                }}
                            />
                        </IconButton>
                        <Box sx={{ marginLeft: "6px" }}>{data.userName}</Box>
                    </Box>
                )}
                {status === "Incomplete" && (
                    <Box display="flex" alignItems="center">
                        <IconButton onClick={handleTogglePaceInfo}>
                            <CustomIcon
                                icon={icons.paceIconGrayedOut}
                                sx={{
                                    fontSize: 20,
                                }}
                            />
                        </IconButton>
                        <Box sx={{ marginLeft: "6px" }}>{data.userName}</Box>
                    </Box>
                )}
            </>
        );
    };

    return (
        <>
            <TableRow className={classes.row} onClick={(event) => handleToggleExpand(event)}>
                <TableCell>
                    <PaceStatus status={data.paceStatus} />
                </TableCell>
                <TableCell>
                    <LinearProgress
                        value={progress}
                        variant="determinate"
                        className={cn([classes.progressBar], {
                            [classes.progressBarComplete]: progress > 99,
                        })}
                    />
                </TableCell>
                <TableCell>{Boolean(data.teams.length) && <TeamChips chips={data.teams} />}</TableCell>
                <TableCell>
                    {data.lastContactedOn !== null
                        ? formatDistanceToNow(new Date(data.lastContactedOn), { addSuffix: true })
                        : ""}
                </TableCell>
                <TableCell>
                    {data.paceStatus === "Incomplete" && (
                        <NavButton className={classes.reminderButton} icon={null} size="small" variant="outlined">
                            Send Reminder
                        </NavButton>
                    )}
                </TableCell>
                <TableCell>{data.paceStatus === "Incomplete" && Checkbox}</TableCell>
                <TableCell>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                        {data.paceStatus !== "Incomplete" && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                        handleShowReport(ev, data.id)
                                    }
                                >
                                    <CustomIcon
                                        className={cn(classes.actionBtn, classes.actionBtn1)}
                                        icon={icons.chart}
                                    />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                        handleDownloadReport(ev, data.id)
                                    }
                                >
                                    <CustomIcon
                                        className={cn(classes.actionBtn, classes.actionBtn1)}
                                        icon={icons.downloadFile}
                                    />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                        handleTransferProfile(ev, data.id)
                                    }
                                >
                                    <CustomIcon
                                        className={cn(classes.actionBtn, classes.actionBtn2)}
                                        icon={icons.cardWithArrows}
                                    />
                                </IconButton>
                            </>
                        )}
                        <IconButton
                            size="small"
                            onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                handleGetInfo(ev, data.id)
                            }
                        >
                            <InfoIcon className={cn(classes.actionBtn, classes.actionBtn2)} />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow className={classes.expandableRow}>
                <TableCell colSpan={7}>
                    <Collapse in={shouldExpand} timeout="auto" unmountOnExit>
                        <Box display="flex" alignItems="center" py={0.6} px={0}>
                            <Box display="flex" alignItems="center" mr={2}>
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark" }}
                                    onClick={(event) => console.log("clicked")}
                                >
                                    <RestoreIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{ color: "secondary.main" }}
                                    onClick={(event) => console.log("clicked")}
                                >
                                    <CustomIcon icon={icons.linkedin} sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Box>
                            {data.email && (
                                <Box display="flex" alignItems="center" mr={2}>
                                    <Typography mr={1}>
                                        {intl.formatMessage({
                                            id: "hubManagement.client.userTable.email",
                                        })}
                                        :
                                    </Typography>
                                    <Typography>{data.email}</Typography>
                                </Box>
                            )}
                            {data.linkedinId && (
                                <Box display="flex" alignItems="center">
                                    <Typography mr={1}>
                                        {intl.formatMessage({
                                            id: "hubManagement.client.userTable.linkedInID",
                                        })}
                                        :
                                    </Typography>
                                    <Typography>{data.linkedinId}</Typography>
                                </Box>
                            )}
                            <IconButton
                                className={classes.toggleCloseBtn}
                                onClick={(event) => handleToggleExpand(event)}
                            >
                                <CustomIcon icon={icons.close} />
                            </IconButton>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default UserTableRow;
